import { type ReactNode } from 'react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { ContentLayout, ContentMain } from '@/components/ContentLayout'
import { InventoryManagerHeader } from '@/features/inventory/admin/components/InventoryManagerHeader'
import { InventoryViewerHeader } from '@/features/inventory/admin/components/InventoryViewerHeader'
import styles from './InventoryManager.module.css'

export interface InventoryManagerProps {
  /**
   * The inventory selector shown as the header's heading.
   */
  heading: ReactNode
  canManageGlobalInventory: boolean
  canManageWorkspaceInventory: boolean
  onAddGlobalItem: () => void
  onAddWorkspaceItem: () => void
  /**
   * The inventory item type filter.
   */
  viewer: ReactNode
  searchText: string
  onSearchTextChange: (text: string) => void
  isGlobalInventory: boolean
  onAddInventoryItem: () => void
  table: ReactNode
  /**
   * The inventory item dialogs.
   */
  children?: ReactNode
}

export function InventoryManager({
  heading,
  canManageGlobalInventory,
  canManageWorkspaceInventory,
  onAddGlobalItem,
  onAddWorkspaceItem,
  viewer,
  searchText,
  onSearchTextChange,
  isGlobalInventory,
  onAddInventoryItem,
  table,
  children,
}: InventoryManagerProps) {
  const { connectRef: scrollRef, scrollYStart: scrollOffset } =
    useScrollOffsets()

  return (
    <div className={styles.root} ref={scrollRef}>
      <InventoryManagerHeader
        scrollOffset={scrollOffset}
        heading={heading}
        canManageGlobalInventory={canManageGlobalInventory}
        canManageWorkspaceInventory={canManageWorkspaceInventory}
        onAddGlobalItem={onAddGlobalItem}
        onAddWorkspaceItem={onAddWorkspaceItem}
      />
      <ContentLayout accountForLabel>
        <ContentMain constrainWidth>
          {viewer}
          <div className={styles.inventoryViewer}>
            <InventoryViewerHeader
              searchText={searchText}
              onSearchTextChange={onSearchTextChange}
              isGlobalInventory={isGlobalInventory}
              onAddInventoryItem={onAddInventoryItem}
            />
            <div className={styles.inventoryTableContainer}>{table}</div>
          </div>
        </ContentMain>
      </ContentLayout>
      {children}
    </div>
  )
}
